import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type Document } from '@/features/documents/Document'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import type { AlertFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { type Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { pluralize } from '@/utils/pluralization-util'

/**
 * View model for confirming the permanent deletion of one or more documents.
 */
export class DeleteDocumentsDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<Id[]>({
    abort$: this.modal.close$,
  })

  private _documents: Document[] = []

  @observable accessor documentCount = 0

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _documentStore: DocumentStore,
  ) {}

  /**
   * Asks the user to confirm, then deletes the documents.
   *
   * @returns The IDs of the documents that were deleted.
   */
  async prompt(documents: Document[], signal?: AbortSignal): Promise<Id[]> {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._documents = documents
      this.documentCount = documents.length
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? []
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    const documents = this._documents
    if (documents.length === 0) {
      return
    }

    const results = await Promise.all(
      documents.map((doc) =>
        this._documentStore.archive(doc).then(
          () => ({ documentLongId: doc.id, success: true }),
          () => ({ documentLongId: doc.id, success: false }),
        ),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failedCount = results.length - successful.length

    if (failedCount < 1) {
      this._newAlert()
        .withState('success')
        .saying(
          'Success!',
          `${successful.length} ${pluralize('document has', successful.length, 'documents have')} been removed!`,
        )
        .autoDismiss(2500)
        .show()
    } else {
      this._newAlert()
        .withState('error')
        .saying(
          `Failed to remove ${failedCount} ${pluralize('document', failedCount)}.`,
        )
        .autoDismiss(2500)
        .show()
    }

    this._prompt.respond(successful.map((r) => r.documentLongId))
    this.modal.close()
  })
}
